import { z } from 'zod';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { Money } from './metric';
import { EntityRef, OwnerRef } from './read-models';

/**
 * Portfolio finance and planning — funding, forecast and team capacity.
 *
 * Financial management is the strongest thing this product does and the one
 * place nothing could be *set*. Budgets and funding arrived only by import,
 * there was no target anybody could enter, funding was single-level and
 * read-only, and there was no forecast — which made the platform a reporting
 * surface over somebody else's spreadsheet rather than the place the plan
 * lives.
 *
 * Three rules are carried in the shapes rather than left to the screens.
 *
 * **A projection is never a measurement.** `forecastToPeriodEnd` sits beside
 * `actualToDate` and carries `forecastMethod` — a sentence naming how it was
 * arrived at — on every row. A forecast whose method is not on the same row as
 * the number gets quoted as though somebody had measured it.
 *
 * **Agreed money and proposed money are different claims.** `approvedFunding`
 * and `draftFunding` are separate fields, and variance is drawn only against
 * the first. A screen that added them would be reporting on neither.
 *
 * **Restricted and absent stay apart.** Every money field is nullable twice
 * over, and the row carries `isRestricted` so the screen can tell a reader
 * "you may not see this" rather than "nobody recorded it". They lead to
 * completely different actions.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 1. Funding and forecast — /v2/funding
// ─────────────────────────────────────────────────────────────────────────────

/** The levels `rel.endpoint_rule` permits `FUNDS_ENTITY` to point at. */
export const FundingLevel = z.enum(['Product', 'ProductGroup', 'ValueStream']);
export type FundingLevel = z.infer<typeof FundingLevel>;

/**
 * Draft is a number somebody typed. Approved is a number somebody else agreed
 * to. Superseded is what a revision leaves behind, kept because the fact that
 * funding moved mid-year is usually the most interesting thing on the page.
 */
export const FundingStatus = z.enum(['Draft', 'Approved', 'Superseded']);
export type FundingStatus = z.infer<typeof FundingStatus>;

export const FundingPlanRow = z.object({
  id: Uuid,
  subject: EntityRef,
  level: FundingLevel,
  periodStart: IsoDate,
  periodEnd: IsoDate,
  amount: Money.nullable(),
  status: FundingStatus,
  /** Why this amount. Null where whoever set it did not say. */
  basis: z.string().nullable(),
  setBy: OwnerRef.nullable(),
  approvedBy: OwnerRef.nullable(),
  approvedAt: Timestamp.nullable(),
  /** The decision this plan entered the approval queue as. */
  decisionId: Uuid.nullable(),
  supersededById: Uuid.nullable(),
});
export type FundingPlanRow = z.infer<typeof FundingPlanRow>;

/**
 * One fundable part of the business: what it was funded for, what it has spent,
 * and where that lands by period end.
 */
export const FundingLine = z.object({
  subject: EntityRef,
  level: FundingLevel,
  /** The live agreed plan. Null where nobody has agreed one — never zero. */
  approvedFunding: Money.nullable(),
  /** A plan awaiting approval, shown apart and never added to the above. */
  draftFunding: Money.nullable(),
  actualToDate: Money.nullable(),
  /**
   * Where spend lands at period end if it continues at the rate so far.
   *
   * Derived, not recorded, and null wherever the derivation cannot be made —
   * no actuals, or a period that has not started.
   */
  forecastToPeriodEnd: Money.nullable(),
  /** How the forecast was arrived at, in a sentence, on the same row. */
  forecastMethod: z.string(),
  /** Approved funding minus forecast. Null when either side is null. */
  forecastVariance: Money.nullable(),
  /** True when the nulls above are an entitlement rather than an absence. */
  isRestricted: z.boolean(),
  /** Present when there is no approved plan, saying what would fix it. */
  unfundedReason: z.string().nullable(),
  planCount: z.number().int().nonnegative(),
});
export type FundingLine = z.infer<typeof FundingLine>;

export const FundingView = z.object({
  level: FundingLevel,
  periodStart: IsoDate,
  periodEnd: IsoDate,
  lines: z.array(FundingLine),
  plans: z.array(FundingPlanRow),
  totals: z.object({
    subjects: z.number().int().nonnegative(),
    funded: z.number().int().nonnegative(),
    /** Subjects spending against no agreed plan — the finding this screen exists for. */
    unfunded: z.number().int().nonnegative(),
    awaitingApproval: z.number().int().nonnegative(),
    approvedFunding: Money.nullable(),
    actualToDate: Money.nullable(),
    forecastToPeriodEnd: Money.nullable(),
  }),
  /** Said once at the top, so every row does not have to repeat it. */
  forecastMethod: z.string(),
});
export type FundingView = z.infer<typeof FundingView>;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Cost attribution — the drawer behind a funding line
// ─────────────────────────────────────────────────────────────────────────────

export const CostAttributionRow = z.object({
  id: Uuid,
  costType: z.string().nullable(),
  capexOpex: z.string().nullable(),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  amount: Money,
  subject: EntityRef.nullable(),
  /** 0–1. What share of this cost has an approved home. */
  allocatedShare: z.number().min(0).max(1),
  /** The rest. Published rather than spread — the product's strongest claim. */
  unallocatedShare: z.number().min(0).max(1),
  targets: z.array(
    z.object({ target: EntityRef.nullable(), share: z.number(), status: z.string() }),
  ),
});
export type CostAttributionRow = z.infer<typeof CostAttributionRow>;

export const CostAttributionView = z.object({
  subject: EntityRef.nullable(),
  rows: z.array(CostAttributionRow),
  totalRows: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  attributed: Money.nullable(),
  unattributed: Money.nullable(),
  isRestricted: z.boolean(),
});
export type CostAttributionView = z.infer<typeof CostAttributionView>;

// ─────────────────────────────────────────────────────────────────────────────
// 3. Capacity against demand — /v2/capacity
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A team's capacity, the work already committed to it, and what that ratio is.
 *
 * `committedDemandItems` is **derived, not recorded**, and the derivation is on
 * the payload because it has to be on the screen: no work item in this estate
 * names a team, so demand is the open work on each product the team delivers,
 * apportioned by the team's recorded share of that product — the same shares
 * the workforce screen apportions FTE through, so the two reconcile.
 */
export const TeamCapacityRow = z.object({
  team: EntityRef,
  owner: OwnerRef.nullable(),
  productCount: z.number().int().nonnegative(),
  /** Internal plus contractor FTE. Null when unrecorded, or Restricted. */
  capacityFte: z.number().nullable(),
  vacancies: z.number().int().nullable(),
  /** Items a team completes in one iteration, where the team records it. */
  capacityPerIteration: z.number().nullable(),
  /** Open work apportioned to this team. Null where no share is recorded. */
  committedDemandItems: z.number().nullable(),
  /** Demand ÷ throughput capacity. Null unless both sides exist. */
  utilisation: z.number().nullable(),
  /** Why there is no utilisation figure. Null when there is one. */
  utilisationUnavailableReason: z.string().nullable(),
  /** How many of the team's products contributed a recorded share. */
  apportionedProducts: z.number().int().nonnegative(),
  isRestricted: z.boolean(),
});
export type TeamCapacityRow = z.infer<typeof TeamCapacityRow>;

export const CapacityView = z.object({
  rows: z.array(TeamCapacityRow),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  /**
   * Open work at the end of each month in the period.
   *
   * Demand can be trended because work items carry dates. Utilisation cannot:
   * capacity is held as snapshots rather than as a series, and the screen says
   * so rather than drawing a line through one point.
   */
  demandTrend: z.array(z.object({ periodEnd: IsoDate, openItems: z.number().int().nonnegative() })),
  totals: z.object({
    teams: z.number().int().nonnegative(),
    teamsWithCapacity: z.number().int().nonnegative(),
    teamsWithDemand: z.number().int().nonnegative(),
    capacityFte: z.number().nullable(),
    committedDemandItems: z.number().nullable(),
    /** Open items belonging to no team's products — counted, never dropped. */
    unapportionedItems: z.number().int().nonnegative(),
  }),
  derivation: z.string(),
  isRestricted: z.boolean(),
});
export type CapacityView = z.infer<typeof CapacityView>;

// ─────────────────────────────────────────────────────────────────────────────
// 4. The scenario catalogue — /v2/scenarios
// ─────────────────────────────────────────────────────────────────────────────

export const ScenarioListRow = z.object({
  scenarioId: Uuid,
  businessCode: z.string().nullable(),
  name: z.string(),
  scenarioType: z.string(),
  subject: EntityRef.nullable(),
  status: z.string(),
  version: z.number().int().positive(),
  isDirty: z.boolean(),
  isReadOnly: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: Timestamp,
  lastCalculatedAt: Timestamp.nullable(),
  clonedFromId: Uuid.nullable(),
  /** The headline the catalogue shows without opening the comparison. */
  headline: z.string(),
});
export type ScenarioListRow = z.infer<typeof ScenarioListRow>;

export const ScenarioCatalogueView = z.object({
  rows: z.array(ScenarioListRow),
  /** What a new consolidation scenario would be modelled over. */
  candidates: z.object({
    applications: z.number().int().nonnegative(),
    withRecordedCost: z.number().int().nonnegative(),
    annualRunCost: Money.nullable(),
    isRestricted: z.boolean(),
  }),
  totals: z.object({
    scenarios: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    needingRecalculation: z.number().int().nonnegative(),
  }),
});
export type ScenarioCatalogueView = z.infer<typeof ScenarioCatalogueView>;
